import {
    ArchiveIcon,
    ArchiveRestoreIcon,
    BellIcon,
    BellOffIcon,
    EllipsisVerticalIcon,
    MailIcon,
    MailOpenIcon,
} from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@rocco/components/dropdown-menu';
import { useConversation } from '../../context';
import { useUpdateConversation } from '../../hooks/use-update-conversation';

/**
 * The more options button for a chat box. Shows a dropdown menu with more
 * options. Renders nothing when the conversation is not yet loaded.
 * @see {@link ChatBox}
 */
export function ChatBoxMoreOptions({
    className,
    onToggleRead,
}: {
    className?: string;
    onToggleRead: () => void;
}) {
    const { conversationID, professionalID, conversation } = useConversation();
    const { archiveConversation, muteConversation } = useUpdateConversation({
        conversationID,
        professionalID,
    });

    if (!conversation) {
        return null;
    }

    const isUnread = conversation.UnreadMessageCount > 0;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex size-[36px] items-center justify-center rounded-full hover:bg-black/10',
                        className,
                    )}
                    aria-label="More options"
                >
                    <EllipsisVerticalIcon className="size-[16px]" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-unset min-w-[180px]"
                align="end"
                side="bottom"
            >
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() => onToggleRead()}
                >
                    {isUnread ? (
                        <MailOpenIcon className="size-[14px]" />
                    ) : (
                        <MailIcon className="size-[14px]" />
                    )}
                    <span>{isUnread ? 'Mark as read' : 'Mark as unread'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() => muteConversation(!conversation.IsMuted)}
                >
                    {conversation.IsMuted ? (
                        <BellIcon className="size-[14px]" />
                    ) : (
                        <BellOffIcon className="size-[14px]" />
                    )}
                    <span>
                        {conversation.IsMuted
                            ? 'Unmute conversation'
                            : 'Mute conversation'}
                    </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() =>
                        archiveConversation(!conversation.IsArchived)
                    }
                >
                    {conversation.IsArchived ? (
                        <ArchiveRestoreIcon className="size-[14px]" />
                    ) : (
                        <ArchiveIcon className="size-[14px]" />
                    )}
                    <span>
                        {conversation.IsArchived
                            ? 'Unarchive conversation'
                            : 'Archive conversation'}
                    </span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
